import React, { useState, useMemo, useEffect } from "react";
import { useLeads } from "../../context/LeadContext";
import { X, Search, User, Building, Mail, Check, Plus } from "lucide-react";
import Button from "../common/Button/Button";
import PaginationControls from "../common/Pagination/PaginationControls";
import {
  FIELD_LIMITS,
  handleSearchInputChange
} from "../../utils/inputValidation";

const LeadSelectionModal = ({
  isOpen,
  onClose,
  onSelect,
  title = "Select Lead",
  description = "Choose a lead to log activity for",
  confirmText = "Log Activity"
}) => {
  const {
    leads,
    loading: leadsLoading,
    loadLeads,
    pagination: leadsPagination,
  } = useLeads();

  const [leadSearchTerm, setLeadSearchTerm] = useState("");
  const [debouncedLeadSearchTerm, setDebouncedLeadSearchTerm] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [leadPage, setLeadPage] = useState(1);
  const [leadLimit, setLeadLimit] = useState(10);

  // Reset modal state when closed/opened
  useEffect(() => {
    if (!isOpen) {
      setLeadPage(1);
      setLeadSearchTerm("");
      setSelectedLeadId("");
      setDebouncedLeadSearchTerm("");
    }
  }, [isOpen]);

  // Debounce lead search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedLeadSearchTerm(leadSearchTerm);
      setLeadPage(1); // Reset page when search term changes
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [leadSearchTerm]);

  // Load leads when the lead selection modal opens or search term changes
  useEffect(() => {
    if (isOpen) {
      const filters = {
        page: leadPage,
        limit: leadLimit,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      if (debouncedLeadSearchTerm && debouncedLeadSearchTerm.trim()) {
        filters.search = debouncedLeadSearchTerm.trim();
      }

      loadLeads(filters);
    }
  }, [
    isOpen,
    debouncedLeadSearchTerm,
    leadPage,
    leadLimit,
    loadLeads,
  ]);

  // Sort by createdAt (newest first) for display
  const displayedLeads = useMemo(() => {
    if (!leads || leads.length === 0) return [];
    return [...leads].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.updatedAt || 0);
      const dateB = new Date(b.createdAt || b.updatedAt || 0);
      return dateB - dateA;
    });
  }, [leads]);

  const getLeadName = (lead) => {
    if (!lead) return "";
    return `${lead.firstName || ""} ${lead.lastName || ""}`.trim() || lead.company || "Unnamed Lead";
  };

  const handleConfirm = () => {
    const selectedLead = leads.find(
      (l) => (l.id || l._id || l.leadId) === selectedLeadId
    );
    if (selectedLead) {
      onSelect(selectedLeadId, selectedLead);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <p className="text-gray-600 text-sm mt-1">{description}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-6 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search leads by name, company, or email..."
              value={leadSearchTerm}
              onChange={(e) => {
                const sanitized = handleSearchInputChange(
                  e,
                  setLeadSearchTerm,
                  FIELD_LIMITS.search
                );
                setLeadSearchTerm(sanitized);
              }}
              maxLength={FIELD_LIMITS.search}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
            />
          </div>
        </div>

        {/* Leads List */}
        <div
          className="flex-1 overflow-y-auto p-6"
          style={{ maxHeight: "calc(3 * (120px + 0.5rem))" }}
        >
          {leadsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                <p className="text-gray-600">Loading leads...</p>
              </div>
            </div>
          ) : displayedLeads.length === 0 ? (
            <div className="text-center py-12">
              <div className="p-3 bg-gray-100 rounded-full mb-4 inline-block">
                <User className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No leads found
              </h3>
              <p className="text-gray-500">
                {leadSearchTerm
                  ? "Try adjusting your search criteria."
                  : "No leads available. Please add leads first."}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {displayedLeads.map((lead) => {
                const leadId = lead.id || lead._id || lead.leadId;
                const isSelected = selectedLeadId === leadId;

                return (
                  <div
                    key={leadId}
                    onClick={() => {
                      setSelectedLeadId(isSelected ? "" : leadId);
                    }}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors overflow-hidden ${
                      isSelected
                        ? "border-agrosaf-green bg-agrosaf-green/10"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 overflow-hidden">
                      <div className="flex items-start space-x-3 flex-1 min-w-0 overflow-hidden">
                        <div
                          className={`p-2 rounded-lg flex-shrink-0 ${
                            isSelected
                              ? "bg-agrosaf-green text-white"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          <User className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <h4
                            className="font-semibold text-gray-900 truncate block capitalize"
                            title={getLeadName(lead)}
                          >
                            {getLeadName(lead)}
                          </h4>
                          {lead.company && (
                            <div className="flex items-center space-x-1 text-sm text-gray-600 mt-1 min-w-0 overflow-hidden">
                              <Building className="h-4 w-4 flex-shrink-0" />
                              <span
                                className="truncate block min-w-0 first-letter:capitalize"
                                title={lead.company}
                              >
                                {lead.company}
                              </span>
                            </div>
                          )}
                          {lead.email && (
                            <div className="flex items-center space-x-1 text-sm text-gray-500 mt-2 min-w-0 overflow-hidden">
                              <Mail className="h-3 w-3 flex-shrink-0" />
                              <span
                                className="truncate block min-w-0 lowercase"
                                title={lead.email}
                              >
                                {lead.email}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="ml-2 flex-shrink-0">
                          <div className="w-5 h-5 rounded-full bg-agrosaf-green flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Lead Pagination */}
        <div className="px-6 pb-4">
          <PaginationControls
            currentPage={leadsPagination.currentPage || 1}
            totalPages={leadsPagination.totalPages || 1}
            totalItems={leadsPagination.totalLeads || 0}
            limit={leadsPagination.limit || 5}
            hasNextPage={leadsPagination.hasNextPage}
            hasPrevPage={leadsPagination.hasPrevPage}
            onPageChange={(page) => setLeadPage(page)}
            onLimitChange={(limit) => {
              setLeadLimit(limit);
              setLeadPage(1);
            }}
            limitOptions={[5, 10, 20, 50, 100]}
          />
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end items-center space-x-3 p-6 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={handleConfirm}
            disabled={!selectedLeadId}
            className="bg-agrosaf-green hover:bg-agrosaf-darkGreen disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LeadSelectionModal;
